import {
	BuildingsIcon,
	CaretUpDownIcon,
	CheckIcon,
	MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDistricts } from "@/api/districts/queries";
import { useCreatedDistricts } from "@/api/reseller";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { PopoverSearchHeader } from "@/components/ui/popover-search-header";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import { useDistrict } from "@/contexts/district-context";
import { useEffectiveRole } from "@/hooks/use-effective-role";
import { useKeyboardListNav } from "@/hooks/use-keyboard-list-nav";

type DistrictLike = { id: number; name: string };

function DistrictDropdown({
	districts,
	isLoading,
	showAll = true,
}: {
	districts: DistrictLike[];
	isLoading: boolean;
	showAll?: boolean;
}) {
	const { t } = useTranslation();
	const { isMobile } = useSidebar();
	const { districtName, setDistrict } = useDistrict();
	const [search, setSearch] = useState("");
	const [open, setOpen] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const options = useMemo(() => {
		const base: Array<DistrictLike | { id: "__all"; name: string }> = [];
		if (showAll)
			base.push({ id: "__all", name: t("districtSelector.allDistricts") });
		return [...base, ...districts];
	}, [districts, showAll, t]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return options;
		return options.filter((o) => o.name.toLowerCase().includes(q));
	}, [options, search]);

	const displayName = districtName ?? t("districtSelector.allDistricts");

	function commit(option: (typeof filtered)[number]) {
		if (option.id === "__all") setDistrict(null);
		else setDistrict(option.name);
		setSearch("");
		setOpen(false);
	}

	const { activeIndex, setActiveIndex, onKeyDown, resetActiveIndex } =
		useKeyboardListNav(filtered, {
			onCommit: commit,
			onEscape: () => setOpen(false),
		});

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="group/trigger cursor-pointer bg-sidebar-accent/40 hover:bg-sidebar-accent/70 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground transition-colors"
							disabled={isLoading}
							aria-label={t("districtSelector.label")}
						>
							<div className="flex size-8 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground ring-1 ring-inset ring-white/10 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:text-white group-data-[collapsible=icon]:ring-0">
								<BuildingsIcon
									aria-hidden="true"
									weight="fill"
									className="size-4"
								/>
							</div>
							<div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
								<span className="truncate font-semibold tracking-tight">
									{displayName}
								</span>
								<span className="truncate text-[11px] uppercase tracking-wider text-sidebar-foreground/60">
									{t("districtSelector.label")}
								</span>
							</div>
							<CaretUpDownIcon
								aria-hidden="true"
								className="ml-auto size-4 text-sidebar-foreground/60 transition-transform duration-200 group-data-[state=open]/trigger:rotate-180 group-data-[collapsible=icon]:hidden"
							/>
						</SidebarMenuButton>
					</PopoverTrigger>
					<PopoverContent
						className="w-(--radix-popover-trigger-width) min-w-72 overflow-hidden p-0 shadow-lg"
						side={isMobile ? "bottom" : "right"}
						align="start"
						sideOffset={8}
						onOpenAutoFocus={(e) => {
							e.preventDefault();
							inputRef.current?.focus();
						}}
					>
						<PopoverSearchHeader
							ref={inputRef}
							value={search}
							onValueChange={(value) => {
								setSearch(value);
								resetActiveIndex();
							}}
							onKeyDown={onKeyDown}
							placeholder={t("districtSelector.searchPlaceholder")}
							showEnterHint
						/>
						<div className="max-h-72 overflow-y-auto py-1" role="listbox">
							{filtered.length === 0 ? (
								<div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
									<MagnifyingGlassIcon
										aria-hidden="true"
										className="size-5 text-muted-foreground/60"
									/>
									<p className="text-sm text-muted-foreground">
										{t("districtSelector.noResults")}
									</p>
								</div>
							) : (
								filtered.map((option, idx) => {
									const isAll = option.id === "__all";
									const isActive = isAll
										? !districtName
										: districtName === option.name;
									const isHighlighted = idx === activeIndex;
									return (
										<button
											type="button"
											key={String(option.id)}
											role="option"
											aria-selected={isActive}
											onMouseEnter={() => setActiveIndex(idx)}
											onClick={() => commit(option)}
											className={`group/row flex w-full items-center gap-2.5 px-2.5 py-2 text-left text-sm transition-colors ${
												isHighlighted
													? "bg-accent/60 text-accent-foreground"
													: "text-foreground/90 hover:bg-accent/40"
											}`}
										>
											<span
												className={`flex size-5 shrink-0 items-center justify-center rounded-full border transition-all ${
													isActive
														? "border-primary bg-primary text-primary-foreground"
														: "border-border/70 bg-transparent"
												}`}
											>
												{isActive && (
													<CheckIcon
														aria-hidden="true"
														weight="bold"
														className="size-3"
													/>
												)}
											</span>
											<span className="flex-1 truncate">{option.name}</span>
											{isAll && (
												<span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
													{districts.length}
												</span>
											)}
											{isHighlighted && !isAll && (
												<span className="hidden text-[10px] text-muted-foreground sm:inline">
													↵
												</span>
											)}
										</button>
									);
								})
							)}
						</div>
					</PopoverContent>
				</Popover>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}

function DistrictSelectorDM() {
	const { data: districts = [], isLoading } = useDistricts();
	const { districtName, setDistrict } = useDistrict();

	useEffect(() => {
		if (districts.length === 0) return;
		const isValid =
			!!districtName && districts.some((d) => d.name === districtName);
		if (!isValid) setDistrict(districts[0].name);
	}, [districtName, districts, setDistrict]);

	return (
		<DistrictDropdown
			districts={districts}
			isLoading={isLoading}
			showAll={false}
		/>
	);
}

function DistrictSelectorReseller() {
	const { data, isLoading } = useCreatedDistricts({ page_size: 100 });
	const districts = data?.results ?? [];
	return <DistrictDropdown districts={districts} isLoading={isLoading} />;
}

export function DistrictSelector() {
	const role = useEffectiveRole();
	if (role === "reseller") return <DistrictSelectorReseller />;
	if (role === "district_manager") return <DistrictSelectorDM />;
	return null;
}
